import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';
import { getActionData } from '../../../utils';

import type { numberField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/numberField_updateWorkItemAllFieldTypesActionRenderer.graphql';

const messages = defineMessages({
	setAction: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.jira-number-field.setStorypoints',
		defaultMessage: 'Set {fieldName} to {fieldValue}',
		description: 'Label for the set story points in the jira number field',
	},
	clearAction: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.jira-number-field.clear-action',
		defaultMessage: 'Clear {fieldName}',
		description: 'Label for the clear action in the jira number field',
	},
});

export type Props = {
	queryRef: numberField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const NumberField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const { fieldKey, fieldValue, fieldReasoning } = getActionData(action);

	const data = useFragment(
		graphql`
			fragment numberField_updateWorkItemAllFieldTypesActionRenderer on JiraNumberField {
				fieldId
				name
				number
			}
		`,
		queryRef,
	);

	const isFieldRenderable = data?.fieldId === fieldKey;

	const isUpdatingAction = Boolean(fieldValue);

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={fieldReasoning}
				fieldName={data?.name}
				current={data?.number ? String(data.number) : undefined}
				suggested={fieldValue ? String(fieldValue) : undefined}
			/>
		),
		[fieldValue, fieldReasoning, data?.name, data?.number],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!data?.fieldId || !data?.name || data?.fieldId !== fieldKey) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.jira-number-field"
		>
			{isUpdatingAction
				? formatMessage(messages.setAction, {
						fieldName: data?.name?.toLowerCase(),
						fieldValue: fieldValue ? String(fieldValue) : undefined,
					})
				: formatMessage(messages.clearAction, {
						fieldName: data?.name?.toLowerCase(),
					})}
		</Text>
	);
};
